<template>
  <div>
    <div class="wrapper">
      <breadcrumb :crumbs="[$t('menu.procures'), $t('text.list')]">
        <el-button
          v-if="Permit.canActionsChargeEdition('mmsProcureManage')"
          type="success"
          size="mini"
          @click="$router.push('/normal/mms/procures/new')"
        >
          + {{ $t("text.create") }}
        </el-button>
        <el-button
          v-if="Permit.canActionsChargeEdition('mmsProcureManage')"
          type="default"
          size="mini"
          @click="$router.push('/normal/mms/procures/import')"
        >
          {{ $t("action.import") }}</el-button
        >
      </breadcrumb>
    </div>
    <narrow
      :narrows="narrows"
      :init-filter="initFilter"
      @narrowSelect="narrowSelect"
    />
    <div class="search with-narrows">
      <el-form
        :inline="true"
        @submit.native.prevent="search"
        :model="searchForm"
        size="small"
      >
        <el-form-item>
          <el-input
            v-model.trim="searchForm.keyword"
            :placeholder="$t('placeholder.serialNoRemark')"
            size="small"
          />
        </el-form-item>

        <el-form-item>
          <el-select
            v-model="searchForm.supplierId"
            :placeholder="$t('menu.suppliers')"
            size="small"
            clearable
            filterable
          >
            <el-option
              v-for="supplier in suppliers"
              :key="supplier.id"
              :label="supplier.name"
              :value="supplier.id"
            />
          </el-select>
        </el-form-item>

        <el-form-item>
          <el-input
            v-model.trim="searchForm.materialSerialNo"
            :placeholder="$t('material.serialNo')"
            size="small"
          />
        </el-form-item>
        <el-form-item>
          <el-input
            v-model.trim="searchForm.materialName"
            :placeholder="$t('material.name')"
            size="small"
          />
        </el-form-item>
        <el-form-item>
          <date-range
            size="small"
            v-model="searchForm"
            type="datetime"
            field="createAt"
            :start-placeholder="$t('rules.startAt', [$t('text.createAt')])"
            :end-placeholder="$t('rules.endAt', [$t('text.createAt')])"
          />
        </el-form-item>
        <el-form-item>
          <date-range
            size="small"
            v-model="searchForm"
            type="datetime"
            field="placeAt"
            :start-placeholder="$t('rules.startAt', [$t('procure.placeAt')])"
            :end-placeholder="$t('rules.endAt', [$t('procure.placeAt')])"
          />
        </el-form-item>
        <el-form-item>
          <date-range
            size="small"
            v-model="searchForm"
            type="datetime"
            field="finishAt"
            :start-placeholder="$t('rules.startAt', [$t('procure.finishAt')])"
            :end-placeholder="$t('rules.endAt', [$t('procure.finishAt')])"
          />
        </el-form-item>
        <el-form-item>
          <el-button
            type="primary"
            icon="el-icon-search"
            native-type="submit"
          />
        </el-form-item>
      </el-form>
    </div>
    <div class="wrapper table-area">
      <sticky :sticky-top="50" :viewport="1024" :is-rem="false" class="sticky">
        <vxe-toolbar ref="recordsToolbar" custom zoom class="table-toolbar">
          <template v-slot:buttons>
            <el-dropdown @command="download">
              <el-button type="default" size="mini">
                {{ $t("action.download") }}
                <i class="el-icon-caret-bottom el-icon--right" />
              </el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="checked">
                  {{ $t("action.downLoadChecked") }}
                </el-dropdown-item>
                <el-dropdown-item command="search">
                  {{ $t("action.downLoadSearch") }}
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
            <span class="pager-total">
              {{ $t("rules.pagerTotal", [pager.total]) }}
            </span>
          </template>
        </vxe-toolbar>
      </sticky>

      <vxe-table
        :data="records"
        @checkbox-change="setSelected"
        @checkbox-all="setSelected"
        :custom-config="{ storage: true, checkMethod: checkColumnMethod }"
        :loading="loading"
        id="materialTable"
        row-id="id"
        ref="recordsTable"
        class="recordsTable"
        size="mini"
        resizable
        round
        auto-resize
        highlight-hover-row
        :scroll-y="{ enabled: false }"
      >
        <vxe-table-column field="checkbox" type="checkbox" width="35" />

        <vxe-table-column
          field="serialNo"
          :title="$t('procure.serialNo')"
          min-width="140"
        >
          <template v-slot="{ row }">
            <a @click="showRow(row)">{{ row.serialNo }}</a>
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="workflowState"
          :title="$t('text.workflowState')"
          min-width="100"
        >
          <template v-slot="{ row }">
            <state-label :state="row.workflowState">
              {{ $t("procureTypes." + row.workflowState) }}
            </state-label>
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="firstSupplierName"
          :title="$t('menu.suppliers')"
          min-width="80"
        />
        <vxe-table-column
          field="handOverAt"
          :title="$t('text.handOverAt')"
          min-width="100"
        />
        <vxe-table-column
          field="verifyAt"
          :title="$t('procure.verifyAt')"
          min-width="80"
        >
          <template v-slot="{ row }">
            {{ row.verifyName }}
            <div class="font10">{{ row.verifyAt }}</div>
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="placeAt"
          :title="$t('procure.placeAt')"
          min-width="80"
        >
          <template v-slot="{ row }">
            {{ row.buyerName }}
            <div class="font10">{{ row.placeAt }}</div>
          </template>
        </vxe-table-column>

        <vxe-table-column
          field="finishAt"
          :title="$t('procure.finishAt')"
          min-width="100"
        />

        <vxe-table-column
          field="remark"
          :title="$t('text.remark')"
          min-width="100"
        >
          <template v-slot="{ row }">
            <!-- <raw-html-limit-number
              :content="row.remark"
              :number="6"
              type="richText"
            /> -->
            <raw-html-limit-number
              :content="row.allRemark"
              :number="6"
              type="richText"
            />
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="createAt"
          :title="$t('text.createAt')"
          min-width="80"
        >
          <template v-slot="{ row }">
            {{ row.ownerName }}
            <div class="font10">{{ row.createdAt }}</div>
          </template>
        </vxe-table-column>

        <vxe-table-column
          v-if="Permit.canActionsChargeEdition('mmsProcureManage')"
          :title="$t('text.action')"
          width="120"
          fixed="right"
        >
          <template v-slot="{ row }">
            <el-button
              v-if="row.verifyAt == null && row.finishAt == null"
              type="text"
              size="mini"
              @click="edit(row)"
              >{{ $t("text.edit") }}</el-button
            >
            <el-popconfirm
              v-if="
                row.finishAt == null &&
                ['newly', 'placed'].includes(row.workflowState)
              "
              :title="$t('prompt.confirmDelete')"
              @confirm="deleteRow(row)"
              class="mgr10"
              :class="
                row.verifyAt == null && row.finishAt == null ? 'mgl10' : ''
              "
            >
              <el-button slot="reference" type="text" size="mini">
                {{ $t("text.delete") }}
              </el-button>
            </el-popconfirm>
            <el-button
              v-if="row.verifyAt == null"
              type="text"
              size="mini"
              :loading="verifyDoing"
              @click="verify(row)"
              >{{ $t("action.verify") }}</el-button
            >
            <el-button
              v-if="row.verifyAt != null && row.placeAt == null"
              type="text"
              size="mini"
              :loading="placeDoing"
              @click="place(row)"
              >{{ $t("action.place") }}</el-button
            >
          </template>
        </vxe-table-column>
      </vxe-table>
      <div class="flex flex-between pager">
        <div class="selected-qty-tips">
          选中了
          <span class="strong">{{ selectRecordQty }}</span>
          条记录
        </div>
        <div>
          <vxe-pager
            size="small"
            :loading="false"
            :background="false"
            :current-page="pager.currentPage"
            :page-size="pager.pageSize"
            :total="pager.total"
            :layouts="pagerConfig.layouts"
            :page-sizes="pagerConfig.pageSizes"
            @page-change="changePager"
          ></vxe-pager>
        </div>
      </div>
    </div>
    <drawer
      :title="$t('text.view')"
      :visible.sync="showDF"
      size="900px"
      direction="rtl"
      :wrapper-closable="false"
      :destroy-on-close="true"
    >
      <show :id="currentRowId" @updateInfo="showClose" />
    </drawer>
    <!-- edit -->
    <el-dialog
      :title="$t('text.edit')"
      :close-on-click-modal="false"
      :visible.sync="editDF"
      width="30%"
    >
      <el-form ref="editForm" :model="editObj" label-width="80px">
        <el-form-item
          :label="$t('text.handOverAt')"
          :prop="'handOverAt'"
          :rules="[
            {
              required: true,
              message: $t('rules.required', [$t('text.handOverAt')]),
              trigger: 'blur',
            },
          ]"
        >
          <el-date-picker
            size="small"
            v-model.trim="editObj.handOverAt"
            type="date"
            style="width: 100%"
            :picker-options="pickerTodayAfterTime"
          />
        </el-form-item>
        <el-form-item :label="$t('text.remark')" :prop="'remark'">
          <el-input
            size="small"
            v-model.trim="editObj.remark"
            type="textarea"
            :placeholder="$t('text.remark')"
            :rows="2"
          />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button
          type="primary"
          plain
          :loading="editDoing"
          size="small"
          @click="editSubmit"
          >{{ $t("text.submit") }}</el-button
        >
        <el-button size="small" @click="editDF = false">{{
          $t("text.close")
        }}</el-button>
      </div>
    </el-dialog>
  </div>
</template>
<script>
import axios from "axios";
import { page, table } from "@/utils/mixins.js";
import Util from "@/utils/index.js";
import DateRange from "@/components/DateRange";

import SupplierMms from "@/api/supplierMms.js";
import Procure from "@/api/procure.js";
import Show from "./show.vue";

export default {
  mixins: [
    page,
    table.pager,
    table.search,
    table.narrow,
    table.checker,
    table.sorter,
    table.basic,
  ],
  components: {
    Show,
    DateRange,
  },
  data() {
    return {
      pickerTodayAfterTime: {
        disabledDate(time) {
          return time.getTime() < Date.now() - 8.64e7;
        },
      },
      Util: Util,

      narrows: [
        [
          {
            label: this.$t("order.passRange"),
            items: Procure.passRange,
          },
          {
            label: this.$t("order.nearRange"),
            items: Procure.nearRange,
          },
        ],
        [
          {
            label: this.$t("text.workflowState"),
            items: Procure.types.map((t) => {
              return {
                key: t,
                label: this.$t("procureTypes." + t),
                condition: { workflowState: t },
              };
            }),
          },
        ],
      ],
      initFilter: null,
      searchForm: {
        serialNo: null,
        supplierId: null,
        createAtStartAt: null,
        createAtEndAt: null,
        placeAtStartAt: null,
        placeAtEndAt: null,
        finishAtStartAt: null,
        finishAtEndAt: null,
      },
      sorter: {
        sortBy: "procures_id",
        sortIndicator: "desc",
      },
      suppliers: [],
      //
      row: null,
      currentRowId: null,
      currentRowIdx: null,
      showDF: false,
      //
      editDF: false,
      editObj: {
        handOverAt: null,
        remark: null,
      },
      editDoing: false,
      //
      verifyDoing: false,
      placeDoing: false,
    };
  },

  created() {
    const self = this;
    this.$nextTick(() => {
      // 手动将表格和工具栏进行关联
      this.$refs.recordsTable.connect(this.$refs.recordsToolbar);
    });
    self.loadingStart();
    axios.all([SupplierMms.findByTenantId()]).then(
      axios.spread(function (suppliers) {
        if (suppliers.status === 200) {
          self.suppliers = suppliers.data;
        } else {
          self.sysError();
        }
        self.loadingEnd();
      })
    );
  },
  methods: {
    verify(row) {
      const self = this;
      Procure.findItems(row.id).then(function (res) {
        if (res.status === 200) {
          const items = res.data;
          const noSupplierIdItems = items.filter((i) => i.supplierId == null);
          if (noSupplierIdItems.length !== 0) {
            self.$message({
              message: self.$t("prompt.existNoSupplierIdItems"),
              type: "warning",
            });
            return;
          }
          self.verifyDoing = true;
          Procure.verify(row.id).then(function (res) {
            self.verifyDoing = false;
            if (res.status === 200) {
              self.$message({
                message: self.$t("prompt.verifySuccess"),
                type: "success",
              });
              self.loadData();
            } else {
              self.sysError();
            }
          });
        } else {
          self.sysError();
        }
      });
    },
    place(row) {
      const self = this;
      self.placeDoing = true;
      Procure.place(row.id).then(function (res) {
        self.placeDoing = false;
        if (res.status === 200) {
          self.$message({
            message: self.$t("prompt.placeSuccess"),
            type: "success",
          });
          self.loadData();
        } else {
          self.sysError();
        }
      });
    },
    // 删除
    deleteRow(row) {
      const self = this;
      if (row.verifyAt == null) {
        self.deleteRowFun(row);
        return;
      }
      Procure.findItems(row.id).then(function (res) {
        if (res.status == 200) {
          let items = res.data;
          let itemsReceive = items.filter(
            (i) =>
              i.receiveQuantity != 0 &&
              i.receiveQuantity != null &&
              i.receiveQuantity != ""
          );
          if (itemsReceive.length != 0) {
            self.$message({
              message: "存在收货的物料，不能删除",
              type: "warning",
            });
            return;
          }
          self.deleteRowFun(row);
        } else {
          self.sysError();
        }
      });
    },
    deleteRowFun(row) {
      const self = this;
      Procure.delete(row.id).then(function (res) {
        if (res.status === 200) {
          self.sysResults(res.data, self.$t("prompt.delSuccess"));
          self.loadData();
        } else {
          self.sysError();
        }
      });
    },

    // 编辑
    edit(row) {
      this.row = row;
      this.editObj.remark = row.remark;
      this.editObj.handOverAt = row.handOverAt;
      this.editDF = true;
    },
    editSubmit() {
      const self = this;
      this.$refs.editForm.validate((valid) => {
        if (valid) {
          self.editObj.handOverAt =
            Util.formatDate(self.editObj.handOverAt) + " 00:00:00";
          self.editDoing = true;
          Procure.update(self.row.id, self.editObj).then(function (res) {
            if (res.status === 200) {
              self.editDoing = false;
              self.editDF = false;
              self.$message({
                message: self.$t("prompt.editSuccess"),
                type: "success",
              });
              self.loadData();
            } else {
              self.sysError();
            }
          });
        } else {
          return false;
        }
      });
    },
    // 下载
    download(type) {
      const self = this;
      if (type == "checked") {
        if (!self.downloadChecked(self.selectRecords)) {
          return;
        }
        const data = {
          currentPage: 1,
          pageSize: self.limitNumber(),
          ids: self.selectRecords.map((c) => c.id),
        };
        self.downloadData(data);
      } else {
        if (!self.downloadSearch(self.pager.total)) {
          return;
        }
        const data = JSON.parse(JSON.stringify(self.combineParams()));
        data.pageSize = self.limitNumber();
        self.downloadData(data);
      }
    },
    downloadData(data) {
      const self = this;
      Procure.itemsExportExcel(data).then(function (res) {
        if (res.data.code === 200) {
          self.$message({
            message: self.$t("prompt.downloadPrompt"),
            type: "success",
          });
          self.loadData();
        } else {
          self.$message.error(self.$t("prompt.downloadErr"));
        }
      });
    },
    //
    showRow(row) {
      this.currentRowId = row.id;
      this.showDF = true;
    },
    showClose() {
      this.loadData();
    },
    //
    dataProvider(params) {
      return Procure.search(params);
    },
  },
};
</script>
